import {
  CaretDoubleLeft,
  CaretDoubleRight,
  CaretLeft,
  CaretRight,
} from "@phosphor-icons/react";
import classNames from "classnames/bind";
import {
  addMonths,
  addYears,
  eachDayOfInterval,
  eachWeekOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isWithinInterval,
  startOfDay,
  startOfMonth,
  subMonths,
  subYears,
} from "date-fns";
import React, { useMemo, useState } from "react";
import { Button } from "../../../Button";
import styles from "../DateSelector.module.scss";
import { NOW } from "../dateSelectorConstants";
import type { DayPickerProps } from "../types";
import TimePicker from "./TimePicker";

const cx = classNames.bind(styles);

const DayPicker = ({
  value,
  onChange,
  onClose,
  isRange,
  min: minDate,
  max: maxDate,
  isWithTime,
  isClearable = false,
}: DayPickerProps) => {
  const [timePickerValue, setTimePickerValue] = useState<Date>(new Date());
  const [isTimeSelectionOpen, setIsTimeSelectionOpen] = useState(false);
  const [endDateOnMouseEnter, setEndDateOnMouseEnter] = useState<
    Date | undefined
  >();

  const [rangeStart, setRangeStart] = useState<Date | undefined>();

  const [baseValue, setBaseValue] = useState(
    value ? (value instanceof Date ? value : value?.from) : NOW
  );

  const options = useMemo(() => {
    const minDay = startOfMonth(baseValue);
    const maxDay = endOfMonth(baseValue);

    return eachWeekOfInterval({ start: minDay, end: maxDay }).reduce(
      (total: Date[], start) => {
        return [
          ...total,
          ...eachDayOfInterval({ start, end: endOfWeek(start) }),
        ];
      },
      []
    );
  }, [baseValue]);

  const onSetDate = (newValue: Date | undefined) => {
    if (!isRange) {
      onChange(newValue);

      onClose();
    } else {
      if (!newValue) {
        onChange(undefined);
        onClose();
        return;
      }

      const shouldSetStart =
        !value?.from ||
        !rangeStart ||
        isBefore(newValue, rangeStart) ||
        (value?.to && isBefore(newValue, value.to)) ||
        (value instanceof Date && isBefore(newValue, value));

      if (shouldSetStart) {
        onChange({
          from: newValue,
          to: undefined,
        });

        setRangeStart(newValue);
      } else {
        if (value) {
          if (isWithTime) {
            onChange({ ...value, to: newValue });
          } else {
            onChange({ ...value, to: endOfDay(newValue) });
          }
        }

        onClose();
      }
    }
  };

  const onClick = (newValue: Date) => {
    if (!isRange) {
      if (isWithTime) {
        setTimePickerValue(newValue);
        setIsTimeSelectionOpen(true);
      } else {
        onSetDate(newValue);
      }
    } else {
      const shouldSetStart =
        !value?.from ||
        !rangeStart ||
        isBefore(newValue, rangeStart) ||
        (value?.to && isBefore(newValue, value.to)) ||
        (value instanceof Date && isBefore(newValue, value));

      if (shouldSetStart) {
        if (isWithTime) {
          setRangeStart(undefined);
          setTimePickerValue(newValue);
          setIsTimeSelectionOpen(true);
        } else {
          onSetDate(newValue);
        }
      } else {
        if (isWithTime) {
          setTimePickerValue(endOfDay(newValue));
          setIsTimeSelectionOpen(true);
        } else {
          onSetDate(newValue);

          onClose();
        }
      }
    }
  };

  const onNextOptionsClick = () => {
    setBaseValue(addMonths(baseValue, 1));
  };

  const onNextYearClick = () => {
    setBaseValue(addYears(baseValue, 1));
  };

  const onPreviousOptionsClick = () => {
    setBaseValue(subMonths(baseValue, 1));
  };

  const onPreviousYearClick = () => {
    setBaseValue(subYears(baseValue, 1));
  };

  const onCloseTimeSelection = () => setIsTimeSelectionOpen(false);

  const onMouseEnter = (newDate: Date) => {
    if (isRange && rangeStart && isAfter(newDate, rangeStart)) {
      setEndDateOnMouseEnter(newDate);
    }
  };

  const onMouseLeave = () => {
    if (endDateOnMouseEnter) {
      setEndDateOnMouseEnter(undefined);
    }
  };

  return (
    <div className={cx("yearPickerContainer")}>
      {isWithTime && isTimeSelectionOpen ? (
        <TimePicker
          value={timePickerValue}
          onClose={onCloseTimeSelection}
          onChange={onSetDate}
          isClearable={isClearable}
          isStart={isRange ? !rangeStart : undefined}
        />
      ) : null}

      <div
        style={{
          display: isWithTime && isTimeSelectionOpen ? "none" : "unset",
        }}
      >
        <div className={cx("yearPickerHeader")}>
          <button
            type="button"
            className={cx("caretButton")}
            onClick={onPreviousYearClick}
          >
            <CaretDoubleLeft />
          </button>
          <button
            type="button"
            className={cx("caretButton")}
            onClick={onPreviousOptionsClick}
          >
            <CaretLeft />
          </button>

          <p className={cx("decadeRange")}>
            {format(options[Math.floor(options.length / 2)], "MMM yyyy")}
          </p>

          <button
            type="button"
            className={cx("caretButton")}
            onClick={onNextOptionsClick}
          >
            <CaretRight />
          </button>

          <button
            type="button"
            className={cx("caretButton")}
            onClick={onNextYearClick}
          >
            <CaretDoubleRight />
          </button>
        </div>
        <div className={cx("dayPickerCalendar")}>
          <p className={cx("dayIndicator")}>S</p>
          <p className={cx("dayIndicator")}>M</p>
          <p className={cx("dayIndicator")}>T</p>
          <p className={cx("dayIndicator")}>W</p>
          <p className={cx("dayIndicator")}>T</p>
          <p className={cx("dayIndicator")}>F</p>
          <p className={cx("dayIndicator")}>S</p>
          {options.map((date) => (
            <button
              type="button"
              key={date.toString()}
              className={cx("calendarItem", {
                isInWindow: isWithinInterval(date, {
                  start: startOfMonth(baseValue),
                  end: endOfMonth(baseValue),
                }),
                isHidden: !isWithinInterval(date, {
                  start: startOfMonth(baseValue),
                  end: endOfMonth(baseValue),
                }),
                ...(isRange && !(value instanceof Date)
                  ? {
                      isSelected:
                        (value?.from &&
                          value?.to &&
                          isWithinInterval(date, {
                            start: value?.from,
                            end: value?.to,
                          })) ||
                        (value?.from &&
                          endDateOnMouseEnter &&
                          isWithinInterval(date, {
                            start: value?.from,
                            end: endDateOnMouseEnter,
                          })),
                      isEndSelected:
                        (value?.from && isSameDay(date, value.from)) ||
                        (value?.to && isSameDay(date, value.to)),
                    }
                  : {
                      isSelected: isSameDay(date, value as Date),
                      isEndSelected: isSameDay(date, value as Date),
                    }),
              })}
              onClick={() => onClick(date)}
              onMouseEnter={() => onMouseEnter(date)}
              onMouseLeave={() => onMouseLeave()}
              disabled={
                (minDate && isBefore(endOfDay(date), minDate)) ||
                (maxDate && isAfter(startOfDay(date), maxDate))
              }
            >
              {format(date, "d")}
            </button>
          ))}
        </div>
        {isClearable && (
          <div className={cx("footer")}>
            <Button
              variant="outline"
              onClick={() => {
                onChange(undefined);
                onClose();
              }}
              className={cx("clear")}
            >
              Clear
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DayPicker;
